import React, { useEffect } from "react";
import {
  useAuthStore,
  selectIsAuthenticated,
  selectLogout,
} from "../../store/useAuth";
import { useLocation, useNavigate } from "react-router-dom";
import { getCookie } from "../../config/auth";
import { getTokenExpiration } from "../../utils/token-utils";

interface RouteGuardProps {
  children: React.ReactNode;
}

// Public routes that don't require authentication
const PUBLIC_ROUTES = ["/login"];

const RouteGuard: React.FC<RouteGuardProps> = ({ children }) => {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const token = useAuthStore((state) => state.token);
  const logout = useAuthStore(selectLogout);
  const location = useLocation();
  const navigate = useNavigate();

  const isPublicRoute = PUBLIC_ROUTES.includes(location.pathname);

  // If not authenticated and not on a public route, redirect to login
  useEffect(() => {
    if (!isAuthenticated && !isPublicRoute) {
      navigate("/login", { replace: true });
    }
  }, [isAuthenticated, isPublicRoute, navigate]);

  // If authenticated but missing auth cookie (expired token), logout and redirect
  useEffect(() => {
    if (isAuthenticated) {
      const cookie = getCookie("authToken");
      if (!cookie) {
        logout();
        navigate("/login", { replace: true });
      }
    }
    // Re-check on route changes
  }, [isAuthenticated, location.pathname, logout, navigate]);

  // End the client session when the JWT expires. Visibility/focus checks cover
  // browsers that throttle timers while the tab or computer is sleeping.
  useEffect(() => {
    if (!isAuthenticated || !token) return;

    const expiration = getTokenExpiration(token);
    if (!expiration) return;

    const expireSession = () => {
      if (Date.now() >= expiration) {
        logout();
        navigate("/login", { replace: true });
      }
    };

    expireSession();
    const timeout = window.setTimeout(
      expireSession,
      Math.min(Math.max(expiration - Date.now(), 0), 2_147_483_647)
    );
    window.addEventListener("focus", expireSession);
    document.addEventListener("visibilitychange", expireSession);

    return () => {
      window.clearTimeout(timeout);
      window.removeEventListener("focus", expireSession);
      document.removeEventListener("visibilitychange", expireSession);
    };
  }, [isAuthenticated, token, logout, navigate]);

  // If authenticated and on login page, redirect to dashboard
  useEffect(() => {
    if (isAuthenticated && isPublicRoute) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, isPublicRoute, navigate]);

  // Show loading or nothing while redirecting
  if (!isAuthenticated && !isPublicRoute) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-2 text-gray-600">Перенаправление...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RouteGuard;
